import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  OpenAccountPayload,
  tradingServices,
  TradingAccount,
  TradingOrder,
  TradingPosition,
} from '../../services/trading-service'
import { Skeleton } from '../../lib/components/Skeleton'
import { SymbolSearch } from './symbol-search'
import { WatchlistCard } from './watchlist-card'
import { TradingChat } from './trading-chat'
import { OrderTicket, SelectedAsset } from './order-ticket'
import { OrdersTable } from './orders-table'
import { isFeatureEnabled } from '../../lib/utils/feature-flags'
import './trading.scss'

const errorMessage = (res: any, fallback: string): string =>
  res?.data?.error?.message || res?.data?.message || fallback

const usd = (value: string | number | null | undefined) =>
  value === null || value === undefined || value === ''
    ? '—'
    : Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD' })

const emptyAccountForm = {
  givenName: '',
  familyName: '',
  dateOfBirth: '',
  taxId: '',
  taxIdType: 'USA_SSN',
  countryOfCitizenship: 'USA',
  countryOfBirth: 'USA',
  countryOfTaxResidence: 'USA',
  fundingSource: 'employment_income',
  phoneNumber: '',
  streetAddress: '',
  city: '',
  state: '',
  postalCode: '',
  country: 'USA',
  isControlPerson: false,
  isAffiliatedExchangeOrFinra: false,
  isPoliticallyExposed: false,
  immediateFamilyExposed: false,
  acceptedAgreements: false,
}

const OpenAccountForm = ({ onOpened }: { onOpened: () => void }) => {
  const [form, setForm] = useState(emptyAccountForm)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const set = (name: string, value: string | boolean) => setForm((f) => ({ ...f, [name]: value }))
  const text = (name: keyof typeof emptyAccountForm, label: string, extra = {}) => (
    <label className='trading-page__field'>
      <span>{label}</span>
      <input
        value={form[name] as string}
        onChange={(e) => set(name, e.target.value)}
        required
        {...extra}
      />
    </label>
  )
  const check = (name: keyof typeof emptyAccountForm, label: string) => (
    <label className='trading-page__check'>
      <input
        type='checkbox'
        checked={form[name] as boolean}
        onChange={(e) => set(name, e.target.checked)}
      />
      <span>{label}</span>
    </label>
  )

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError('')
    const { streetAddress, fundingSource, state, ...rest } = form
    const payload: OpenAccountPayload = {
      ...rest,
      state: state || undefined,
      streetAddress: [streetAddress],
      fundingSource: [fundingSource],
    }
    const res = await tradingServices.openAccount(payload)
    setBusy(false)
    if (res?.status === 201) onOpened()
    else setError(errorMessage(res, 'Could not open the account. Check your details.'))
  }

  return (
    <form className='trading-page__card' onSubmit={submit} data-testid='trading-open-account'>
      <h2>Open a brokerage account</h2>
      <p>
        Trade US-listed stocks and ETFs through our brokerage partner Alpaca. Your details are sent
        to Alpaca to verify your identity.
      </p>
      <div className='trading-page__grid'>
        {text('givenName', 'First name')}
        {text('familyName', 'Last name')}
        {text('dateOfBirth', 'Date of birth', { type: 'date' })}
        {text('phoneNumber', 'Phone (with country code)')}
        {text('taxId', 'Tax ID')}
        <label className='trading-page__field'>
          <span>Tax ID type</span>
          <select value={form.taxIdType} onChange={(e) => set('taxIdType', e.target.value)}>
            <option value='USA_SSN'>US SSN</option>
            <option value='IND_PAN'>India PAN</option>
            <option value='NOT_SPECIFIED'>Other</option>
          </select>
        </label>
        {text('countryOfCitizenship', 'Citizenship (3-letter code)', { maxLength: 3 })}
        {text('countryOfBirth', 'Country of birth (3-letter code)', { maxLength: 3 })}
        {text('countryOfTaxResidence', 'Tax residence (3-letter code)', { maxLength: 3 })}
        <label className='trading-page__field'>
          <span>Source of funds</span>
          <select value={form.fundingSource} onChange={(e) => set('fundingSource', e.target.value)}>
            <option value='employment_income'>Employment income</option>
            <option value='investments'>Investments</option>
            <option value='inheritance'>Inheritance</option>
            <option value='business_income'>Business income</option>
            <option value='savings'>Savings</option>
            <option value='family'>Family</option>
          </select>
        </label>
        {text('streetAddress', 'Street address')}
        {text('city', 'City')}
        <label className='trading-page__field'>
          <span>State / region</span>
          <input value={form.state} onChange={(e) => set('state', e.target.value)} />
        </label>
        {text('postalCode', 'Postal code')}
        {text('country', 'Country (3-letter code)', { maxLength: 3 })}
      </div>
      <div className='trading-page__checks'>
        {check('isControlPerson', 'I am a control person of a publicly traded company')}
        {check('isAffiliatedExchangeOrFinra', 'I am affiliated with an exchange or FINRA')}
        {check('isPoliticallyExposed', 'I am a politically exposed person')}
        {check('immediateFamilyExposed', 'An immediate family member is politically exposed')}
        {check(
          'acceptedAgreements',
          'I accept the Alpaca customer agreement and account agreement',
        )}
      </div>
      {error ? (
        <p className='trading-page__error' role='alert'>
          {error}
        </p>
      ) : null}
      <button
        type='submit'
        className='trading-page__btn'
        disabled={busy || !form.acceptedAgreements}
      >
        {busy ? 'Submitting…' : 'Open account'}
      </button>
    </form>
  )
}

const POSITION_HEADERS = ['Symbol', 'Shares', 'Avg cost', 'Market value', 'Unrealized P/L']
const ORDER_HEADERS = ['Symbol', 'Side', 'Type', 'Size', 'Status', 'Filled']

/** Table headers with shimmering rows while the data loads. */
const SkeletonTable = ({ headers, rows = 3 }: { headers: string[]; rows?: number }) => (
  <table className='trading-page__table'>
    <thead>
      <tr>
        {headers.map((h) => (
          <th key={h}>{h}</th>
        ))}
      </tr>
    </thead>
    <tbody>
      {Array.from({ length: rows }, (_, r) => (
        <tr key={r}>
          {headers.map((h) => (
            <td key={h}>
              <Skeleton width='70%' />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  </table>
)

const TradingContainer = () => {
  // Account first (header and balances), then positions and orders.
  const [loading, setLoading] = useState(true)
  const [listsLoading, setListsLoading] = useState(true)
  const [account, setAccount] = useState<TradingAccount | null>(null)
  const [orders, setOrders] = useState<TradingOrder[]>([])
  const [positions, setPositions] = useState<TradingPosition[]>([])
  const [loadError, setLoadError] = useState('')
  const [selected, setSelected] = useState<SelectedAsset | null>(null)
  const ticketRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  const pickFromWatchlist = (asset: SelectedAsset) => {
    setSelected(asset)
    ticketRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  const load = useCallback(async () => {
    const acc = await tradingServices.getAccount()
    if (acc?.status === 404) {
      setAccount(null)
      setLoading(false)
      setListsLoading(false)
      return
    }
    if (acc?.status !== 200) {
      setLoadError(errorMessage(acc, 'Trading is unavailable right now.'))
      setLoading(false)
      setListsLoading(false)
      return
    }
    setLoadError('')
    setAccount(acc.data)
    setLoading(false)
    if (acc.data.status === 'ACTIVE') {
      const [ord, pos] = await Promise.all([
        tradingServices.getOrders(),
        tradingServices.getPositions(),
      ])
      if (ord?.status === 200) setOrders(ord.data)
      if (pos?.status === 200) setPositions(pos.data)
    }
    setListsLoading(false)
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  if (loading) {
    return (
      <div className='trading-page' aria-busy='true'>
        <header className='trading-page__header'>
          <h1>Trade stocks</h1>
          <p>
            <Skeleton width='260px' />
          </p>
        </header>
        <section className='trading-page__stats'>
          <div>
            <span>Buying power</span>
            <strong>
              <Skeleton width='120px' height='24px' />
            </strong>
          </div>
          <div>
            <span>Cash</span>
            <strong>
              <Skeleton width='120px' height='24px' />
            </strong>
          </div>
        </section>
        <section className='trading-page__card'>
          <h2>Positions</h2>
          <SkeletonTable headers={POSITION_HEADERS} />
        </section>
        <section className='trading-page__card'>
          <h2>Orders</h2>
          <SkeletonTable headers={ORDER_HEADERS} />
        </section>
      </div>
    )
  }
  if (loadError) {
    return (
      <div className='trading-page'>
        <p className='trading-page__error' role='alert'>
          {loadError}
        </p>
      </div>
    )
  }
  if (!account) {
    return (
      <div className='trading-page'>
        <OpenAccountForm onOpened={load} />
      </div>
    )
  }

  return (
    <div className='trading-page'>
      <header className='trading-page__header'>
        <h1>Trade stocks</h1>
        <p>
          Account {account.accountNumber || '—'} · <strong>{account.status}</strong>
          {isFeatureEnabled('publicWallet') ? (
            <>
              {' · '}
              <a href='/dashboard/wallet-public'>Wallet</a>
            </>
          ) : null}
        </p>
      </header>

      {account.status !== 'ACTIVE' ? (
        <div className='trading-page__card'>
          Your brokerage account is {account.status.toLowerCase().replace(/_/g, ' ')}. You can trade
          once it is active.
          <div>
            <button type='button' className='trading-page__btn' onClick={() => void load()}>
              Refresh status
            </button>
          </div>
        </div>
      ) : (
        <>
          <section className='trading-page__stats'>
            <div>
              <span>Buying power</span>
              <strong>{usd(account.buyingPower)}</strong>
            </div>
            <div>
              <span>Cash</span>
              <strong>{usd(account.cash)}</strong>
            </div>
          </section>

          <section className='trading-page__card' data-testid='trading-find-stock'>
            <h2>Find a stock</h2>
            <SymbolSearch
              id='find-stock'
              label='Search US stocks and ETFs'
              selected={null}
              onSelect={(asset) => {
                if (asset) navigate(`/dashboard/trading/${asset.symbol}`)
              }}
            />
          </section>

          {isFeatureEnabled('chatPublicTrading') ? <TradingChat onOrderPlaced={load} /> : null}

          <WatchlistCard onPick={pickFromWatchlist} />

          <div ref={ticketRef}>
            <OrderTicket
              selected={selected}
              onSelect={setSelected}
              onPlaced={load}
              positions={positions}
            />
          </div>

          <section className='trading-page__card'>
            <h2>Positions</h2>
            {listsLoading ? (
              <SkeletonTable headers={POSITION_HEADERS} />
            ) : positions.length === 0 ? (
              <p>No open positions.</p>
            ) : (
              <table className='trading-page__table'>
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>Shares</th>
                    <th>Avg cost</th>
                    <th>Market value</th>
                    <th>Unrealized P/L</th>
                  </tr>
                </thead>
                <tbody>
                  {positions.map((p) => (
                    <tr key={p.symbol}>
                      <td>
                        <Link to={`/dashboard/trading/${p.symbol}`}>{p.symbol}</Link>
                      </td>
                      <td>{p.qty}</td>
                      <td>{usd(p.avg_entry_price)}</td>
                      <td>{usd(p.market_value)}</td>
                      <td className={Number(p.unrealized_pl) < 0 ? 'is-loss' : 'is-gain'}>
                        {usd(p.unrealized_pl)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section className='trading-page__card'>
            <h2>Orders</h2>
            {listsLoading ? (
              <SkeletonTable headers={ORDER_HEADERS} />
            ) : orders.length === 0 ? (
              <p>No orders yet.</p>
            ) : (
              <OrdersTable orders={orders} onChanged={load} />
            )}
          </section>
        </>
      )}
      <p className='trading-page__disclaimer'>
        Brokerage services are provided by Alpaca Securities LLC, member FINRA/SIPC. Investing
        involves risk, including loss of principal.
      </p>
    </div>
  )
}

export { TradingContainer }
